import React from 'react';
import { useCampaignTypes } from '@hooks/api/useCampaignTypes';
import { useLeadScraperContext } from '@pages/LeadScraper/context';
import { StepContentProps } from '@pages/LeadScraper/types';
import styled, { css } from 'styled-components';

import { Heading, Radio, Spinner, Stack, Text } from '@components/theme-v2';

// ═══════════════════════════════════════════════════════════════
// ICONS (Custom SVG replacements for MUI icons)
// ═══════════════════════════════════════════════════════════════

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// ShoppingCartIcon removed - e-commerce category removed (doesn't produce lead contact data)

const HomeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9,22 9,12 15,12 15,22" />
  </svg>
);

const WorkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);

const PeopleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const StorageIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const Container = styled.div`
  width: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const TypeCard = styled.div<{ $selected: boolean; $accentColor: string }>`
  display: flex;
  align-items: center;
  padding: var(--sk-space-4);
  background: var(--sk-surface-default);
  border: 2px solid ${props => (props.$selected ? props.$accentColor : 'var(--sk-surface-border)')};
  border-radius: var(--sk-radius-lg);
  cursor: pointer;
  transition: all 0.3s ease;

  ${props =>
    props.$selected &&
    css`
      box-shadow: 0 0 20px ${props.$accentColor}40;
    `}

  &:hover {
    border-color: ${props => props.$accentColor};
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
`;

const IconWrapper = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$color}20;
  border: 1px solid ${props => props.$color}40;
  border-radius: var(--sk-radius-default);
  color: ${props => props.$color};
  margin: 0 var(--sk-space-4);

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
`;

const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sk-space-2);
  margin-top: var(--sk-space-2);
`;

const FeatureItem = styled.span<{ $color: string }>`
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-xs);
  color: ${props => props.$color};

  &::before {
    content: '• ';
  }
`;

// ═══════════════════════════════════════════════════════════════
// TYPES & HELPERS
// ═══════════════════════════════════════════════════════════════

interface CampaignType {
  id: string;
  name: string;
  description: string;
  features: string[];
  icon: React.FC;
  color: string;
}

interface CampaignTypeStepProps {
  value: string;
  onChange: (type: string) => void;
}

const getIconAndColor = (typeName: string): { icon: React.FC; color: string } => {
  const typeNameLower = typeName.toLowerCase();
  // Lead generation campaign types only
  if (typeNameLower.includes('local') || typeNameLower.includes('search')) {
    return { icon: SearchIcon, color: '#00ff7f' };
  }
  if (typeNameLower.includes('real') || typeNameLower.includes('estate')) {
    return { icon: HomeIcon, color: '#00c8e0' };
  }
  if (typeNameLower.includes('job') || typeNameLower.includes('career')) {
    return { icon: WorkIcon, color: '#a78bfa' };
  }
  if (typeNameLower.includes('social') || typeNameLower.includes('media')) {
    return { icon: PeopleIcon, color: '#00c8e0' };
  }
  return { icon: StorageIcon, color: '#94a3b8' };
};

const getFeatures = (typeName: string): string[] => {
  const typeNameLower = typeName.toLowerCase();
  // Lead generation focused features - all produce contact data
  if (typeNameLower.includes('local') || typeNameLower.includes('search')) {
    return ['Business contacts', 'Email & phone', 'Reviews & ratings'];
  }
  if (typeNameLower.includes('real') || typeNameLower.includes('estate')) {
    return ['Agent contacts', 'Property listings', 'Broker emails'];
  }
  if (typeNameLower.includes('job')) {
    return ['Recruiter contacts', 'Company emails', 'Hiring managers'];
  }
  if (typeNameLower.includes('social')) {
    return ['Business profiles', 'Contact info', 'Engagement data'];
  }
  return ['Contact extraction', 'Email & phone', 'Export options'];
};

const getDescription = (typeName: string): string => {
  const typeNameLower = typeName.toLowerCase();
  // Lead generation focused descriptions
  if (typeNameLower.includes('local') || typeNameLower.includes('search')) {
    return 'Extract business contacts from Google Maps, Yelp & directories';
  }
  if (typeNameLower.includes('real') || typeNameLower.includes('estate')) {
    return 'Gather agent & broker contact info from property listings';
  }
  if (typeNameLower.includes('job')) {
    return 'Find recruiter & company contacts from job postings';
  }
  if (typeNameLower.includes('social')) {
    return 'Extract business contacts from social media profiles';
  }
  return 'Collect contact data for email campaigns';
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════

export const CampaignTypeStep: React.FC<CampaignTypeStepProps> = ({ value, onChange }) => {
  const { data: apiCampaignTypes, isLoading } = useCampaignTypes();

  const campaignTypes: CampaignType[] =
    apiCampaignTypes
      ?.filter(apiType => apiType.type_name && apiType.campaign_type_id)
      .map(apiType => {
        const typeName = apiType.type_name!;
        const { icon, color } = getIconAndColor(typeName);
        return {
          id: String(apiType.campaign_type_id),
          name: typeName,
          description: getDescription(typeName),
          features: getFeatures(typeName),
          icon,
          color,
        };
      }) || [];

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner size="lg" />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Heading $level={4} style={{ marginBottom: 'var(--sk-space-3)' }}>
        Select Scraper Category
      </Heading>
      <Text $color="secondary" style={{ marginBottom: 'var(--sk-space-6)' }}>
        Choose the type of data you want to scrape
      </Text>

      <Stack $gap={4}>
        {campaignTypes.map(type => {
          const Icon = type.icon;
          const isSelected = value === type.id;

          return (
            <TypeCard key={type.id} $selected={isSelected} $accentColor={type.color} onClick={() => onChange(type.id)}>
              <Radio
                checked={isSelected}
                onChange={() => onChange(type.id)}
                style={{ marginRight: 'var(--sk-space-2)' }}
              />
              <IconWrapper $color={type.color}>
                <Icon />
              </IconWrapper>
              <ContentWrapper>
                <Text $weight={600} style={{ marginBottom: 'var(--sk-space-1)' }}>
                  {type.name}
                </Text>
                <Text $size="sm" $color="secondary">
                  {type.description}
                </Text>
                <FeatureList>
                  {type.features.map(feature => (
                    <FeatureItem key={feature} $color={type.color}>
                      {feature}
                    </FeatureItem>
                  ))}
                </FeatureList>
              </ContentWrapper>
            </TypeCard>
          );
        })}
      </Stack>
    </Container>
  );
};

// Collapsed view styled component
const CollapsedCard = styled.div<{ $active: boolean }>`
  padding: var(--sk-space-6);
  border-radius: var(--sk-radius-lg);
  border: 1px solid ${props => (props.$active ? 'var(--sk-cyan-500)' : 'var(--sk-surface-border)')};
  background: var(--sk-surface-default);
`;

const CollapsedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CollapsedContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-2);
`;

const EditButton = styled.button`
  background: transparent;
  border: none;
  color: var(--sk-cyan-400);
  cursor: pointer;
  padding: var(--sk-space-2);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--sk-cyan-300);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ width: 24, height: 24, color: 'var(--sk-green-500)' }}
  >
    <path
      fillRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
      clipRule="evenodd"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const CampaignTypeSelection: React.FC<StepContentProps> = ({ handleNext, handleBack, isActive, isCompleted }) => {
  const { campaignTypeForm } = useLeadScraperContext();
  const { data: apiCampaignTypes } = useCampaignTypes();
  const { campaignType } = campaignTypeForm.watch();

  const handleChange = (type: string) => {
    campaignTypeForm.setValue('campaignType', type);
    handleNext();
  };

  // Get the selected campaign type name
  const selectedTypeName =
    apiCampaignTypes?.find(t => String(t.campaign_type_id) === campaignType)?.type_name || 'Not selected';

  // Show collapsed card when completed but not active
  if (!isActive) {
    return (
      <CollapsedCard $active={false}>
        <CollapsedHeader>
          <CollapsedContent>
            {isCompleted && <CheckIcon />}
            <div>
              <Text $weight={600} $color="primary">
                Campaign Type
              </Text>
              <Text $size="sm" $color="secondary">
                {isCompleted ? selectedTypeName : 'Select your campaign type'}
              </Text>
            </div>
          </CollapsedContent>
          {isCompleted && (
            <EditButton onClick={handleBack}>
              <EditIcon />
            </EditButton>
          )}
        </CollapsedHeader>
      </CollapsedCard>
    );
  }

  return <CampaignTypeStep value={campaignType || ''} onChange={handleChange} />;
};

export default CampaignTypeSelection;
